/**
 * Billing-period model for the marketing pricing surfaces (monthly ↔ annual).
 *
 * The annual figure is the −10% annual-prepay rate carried forward from
 * ADR-0103 into ADR-0149. The discount fraction is the single source of truth
 * `ANNUAL_PREPAY_DISCOUNT_FRACTION` (0.10) from `@syncanix/shared` — never a
 * literal here — so the page can never drift off the billed rate. Only the
 * 1-year (10%) term is surfaced on the page; the 2-/3-year (18/25%) terms are
 * sales-led and intentionally NOT on the toggle (ADR-0103).
 */
import { ANNUAL_PREPAY_DISCOUNT_FRACTION } from '@syncanix/shared';

export type BillingPeriod = 'monthly' | 'annual';

/** Ordered periods — drives the segmented control + its keyboard traversal. */
export const BILLING_PERIODS: readonly BillingPeriod[] = ['monthly', 'annual'];

/** The annual-prepay saving as a whole-percent integer, for display (10). */
export const ANNUAL_SAVE_PERCENT = Math.round(ANNUAL_PREPAY_DISCOUNT_FRACTION * 100);

/**
 * The annual monthly-equivalent price for a monthly USD list figure.
 *
 * Rounding rule: monthly × (1 − annual-prepay fraction), then round to the
 * NEAREST WHOLE US DOLLAR (`Math.round`). Whole-dollar is the one consistent
 * rule across every tier — $99→$89, $299→$269, $999→$899, $2,999→$2,699.
 * `uc-tiers.ts` defines no rounding of its own, so this is the single rule.
 */
export function annualMonthlyUsd(monthlyUsd: number): number {
  return Math.round(monthlyUsd * (1 - ANNUAL_PREPAY_DISCOUNT_FRACTION));
}

/** Format a whole-USD integer as a `$`-prefixed, thousands-grouped label. */
export function formatUsd(usd: number): string {
  return `$${usd.toLocaleString('en-US')}`;
}
